import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Autocomplete,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  createUser,
  connectUserToTier,
  checkUserExists,
} from "../../services/userService";
import { provinces, citiesByProvince } from "../../data/canadianCities";
import { Visibility, VisibilityOff } from "@mui/icons-material"; // Import icons

const AgentSignup = ({ open: modalShow, onClose: handleClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    city: "",
    province: "BC",
    brokerage: "",
    email: "",
    mobileNumber: "",
    productType: "",
    promoCode: "",
    photo: null,
    type: "RLPVIAgents",
    password: "",
    passwordVerify: "",
  });

  const [errors, setErrors] = useState({});
  const [, setFormError] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [successModalShow, setSuccessModalShow] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const togglePasswordVerifyVisibility = () =>
    setShowPasswordVerify((prev) => !prev);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (formData.province) {
      setCityOptions(citiesByProvince[formData.province] || []);
    }
  }, [formData.province]);

  const base64ToFile = async (base64, filename) => {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], filename, { type: "image/jpeg" });
  };

  const handleEmailCheck = async () => {
    // Email validation
    if (!emailRegex.test(formData.email)) {
      setErrors({ ...errors, email: "Please enter a valid email address" });
      return;
    }

    const result = await checkUserExists(formData.email);

    if (result.found && result.user) {
      const results = await connectUserToTier(result.user._id, "RLPVIAgents");
      if (results.ok) {
        setUserExists(result.user._id);
        setEmailVerified(true);
      } else {
        setErrors({ ...errors, form: results.error });
      }
    } else {
      setEmailVerified(true);
    }
  };
  const handleImageResize = (base64Image) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Image;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set target size
        const maxSize = 300; // Set image size (width & height)
        canvas.width = maxSize;
        canvas.height = maxSize;

        // Draw resized image on canvas
        ctx.drawImage(img, 0, 0, maxSize, maxSize);

        // Convert to compressed Base64
        let quality = 0.8; // Start with 80% quality
        let resizedBase64 = canvas.toDataURL("image/jpeg", quality);

        // Ensure file size < 150KB
        while (resizedBase64.length > 150000 && quality > 0.5) {
          quality -= 0.1;
          resizedBase64 = canvas.toDataURL("image/jpeg", quality);
        }

        resolve(resizedBase64);
      };
    });
  };
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file" && files[0]) {
      const file = files[0];

      // Ensure it's an image
      if (!file.type.startsWith("image/")) {
        setErrors({ ...errors, photo: "Please upload a valid image file" });
        return;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        const resizedImage = await handleImageResize(event.target.result);
        setFormData((prev) => ({ ...prev, photo: resizedImage }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    console.log("Form data:", formData);

    // Password confirmation validation
    if (formData.password !== formData.passwordVerify) {
      setErrors({ ...errors, passwordVerify: "Passwords do not match" });
      return;
    }

    if (formData.password.length < 8) {
      setErrors({
        ...errors,
        password: "Password must be at least 8 characters long",
      });
      return;
    }

    if (!formData.photo) {
      setFormError("Please upload a headshot.");
      return;
    }

    try {
      const photoFile = await base64ToFile(formData.photo, "profile-image.jpg");

      // Create FormData and append all form fields
      const formDataToSend = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key !== "photo") {
          formDataToSend.append(key, value); // Skips empty values
        }
      });

      // Append the photo file
      formDataToSend.append("photo", photoFile);

      // Log all FormData values before sending
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      const results = await createUser(formDataToSend);

      if (results.success) {
        setSuccessModalShow(true);
      } else {
        setErrors({ ...errors, form: results.error });
      }
    } catch (error) {
      setErrors({ ...errors, form: error.message });
    }
  };

  return (
    <>
      <Dialog open={modalShow} onClose={handleClose} fullWidth maxWidth="md">
        <form onSubmit={handleFormSubmit}>
          <DialogTitle sx={{ bgcolor: "#1976d2", color: "#fff" }}>
            Join RLPVIAgents For FREE
          </DialogTitle>
          <DialogContent dividers>
            {!emailVerified ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Email Address"
                  type="email"
                  name="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
                <Button
                  onClick={handleEmailCheck}
                  variant="contained"
                  color="primary"
                >
                  Continue...
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {userExists ? (
                  <Typography>
                    Welcome back! We've found your account already in REACirc.
                    You're being set up on RLPVIAgents.<br></br>
                    Check your Email for a link to access RLPVIAgents on
                    REACirc.
                  </Typography>
                ) : (
                  <>
                    <TextField
                      label="Email Address"
                      type="email"
                      name="email"
                      fullWidth
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (errors.email) setErrors({ ...errors, email: "" }); // Clear error when user types
                      }}
                      required
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={emailVerified} // Disable after verification
                    />
                    <TextField
                      label="First Name"
                      name="firstName"
                      fullWidth
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                    <TextField
                      label="Last Name"
                      name="lastName"
                      fullWidth
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                    <TextField
                      label="Brokerage Name"
                      name="brokerage"
                      fullWidth
                      value={formData.brokerage}
                      onChange={handleChange}
                      required
                    />
                    <Autocomplete
                      options={provinces}
                      getOptionLabel={(option) => (option ? option.label : "")}
                      value={
                        provinces.find(
                          (province) => province.value === formData.province
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          province: newValue ? newValue.value : "",
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Province"
                          required
                        />
                      )}
                    />
                    <Autocomplete
                      freeSolo
                      options={cityOptions.map((option) => option.value)}
                      onChange={(event, newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          city: newValue,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select or enter city"
                          required
                        />
                      )}
                    />
                    <TextField
                      label="Create a Password"
                      type={showPassword ? "text" : "password"} // Use state here
                      name="password"
                      fullWidth
                      value={formData.password}
                      onChange={handleChange}
                      required
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Confirm Password"
                      type={showPasswordVerify ? "text" : "password"} // Use state here
                      name="passwordVerify"
                      fullWidth
                      value={formData.passwordVerify}
                      onChange={handleChange}
                      required
                      error={!!errors.passwordVerify}
                      helperText={errors.passwordVerify}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVerifyVisibility}
                              edge="end"
                            >
                              {showPasswordVerify ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button variant="outlined" component="label">
                      Upload Headshot
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        name="photo"
                        onChange={handleChange}
                      />
                    </Button>
                    {/* Show error message if image is invalid */}
                    {errors.photo && (
                      <Typography color="error" variant="body2">
                        {errors.photo}
                      </Typography>
                    )}

                    {formData.photo && (
                      <Box
                        component="img"
                        src={formData.photo}
                        alt="Preview"
                        sx={{ maxWidth: "200px", mt: 1, borderRadius: "8px" }}
                      />
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {!userExists && emailVerified ? (
              <>
                <Button onClick={handleClose}>Cancel</Button>
                <Button type="submit" variant="contained" color="primary">
                  Create Account
                </Button>
              </>
            ) : (
              <Button onClick={handleClose}>Close</Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={successModalShow}
        onClose={() => setSuccessModalShow(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ bgcolor: "#1976d2", color: "#fff" }}>
          Success! Your Account Has Been Created on RLPVIAgents.
        </DialogTitle>
        <DialogContent>
          <>
            <Typography variant="h6" align="center">
              Check your email for a link to access RLPVIAgents on REACirc.
            </Typography>
            <Typography variant="body2" align="center">
              If you forget your password, you can reset it from the login page.
            </Typography>
          </>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSuccessModalShow(false);
              handleClose();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentSignup;
